import React, {useEffect, useState} from "react";
import * as yup from "yup";
import * as Sentry from "@sentry/react";
import {useFormik} from "formik";
import {Col, Form, Modal, Row, Input, Button} from "antd";
import {useTranslation} from "react-i18next";
import {useStore} from "../../../../../hooks/useStore";
import {UploadOutlined} from "@ant-design/icons";
import {UploadInvoiceModal} from "../uploadInvoiceModal/UploadInvoiceModal";
import {DeleteOutlined} from "@ant-design/icons";

export const CreditUpdateModal = ({subcompanyId, visibility, onCancel}) => {
    const {t} = useTranslation(["subcompanyDetail"]);
    const stores = useStore();
    const [initialValues, setInitialValues] = useState({
        creditAmount: 0,
        transactionPhoto: null,
        description: ""
    }); // Set initial credit amount
    const [updateFileUpdateVisibilty, setFileUpdateVisibility] = useState(false);

    const [validationSchema, setValidationSchema] = useState(
        yup.object().shape({
            creditAmount: yup.number().required(
                t("subcompanyDetail.credit.validations.creditRequired", {
                    ns: "subcompanyDetail"
                })
            )
        })
    );
    const [confirmLoading, setConfirmLoading] = useState(false);

    const fileUpload = () => {
        setFileUpdateVisibility(true);
    };

    const formik = useFormik({
        initialValues,
        validationSchema,
        enableReinitialize: true,
        onSubmit: async values => {
            setConfirmLoading(true);
            try {
                const {creditAmount, transactionPhoto, description} = values;

                await stores.companyStore.updateSubCompanyCredit({
                    subCompanyId: subcompanyId,
                    creditAmount,
                    transactionPhoto,
                    description
                });

                onCancel(true);
            } catch (error) {
                Sentry.captureException(error);
            } finally {
                setConfirmLoading(false);
            }
        }
    });

    const {values, errors, touched, handleSubmit, handleChange, resetForm, setFieldValue} = formik;

    const reset = () => {
        setInitialValues({creditAmount: null, description: ""});
        resetForm();
    };

    const closeModals = () => {
        setFileUpdateVisibility(false);
    };

    useEffect(() => reset(), [visibility]);

    return (
        <Modal
            title={t("subcompanyDetail.credit.credit", {ns: "subcompanyDetail"})}
            visible={visibility}
            onCancel={() => {
                onCancel(false);
            }}
            onOk={handleSubmit}
            okButtonProps={{disabled: !values.creditAmount}}
            confirmLoading={confirmLoading}
            okText={t("modal.saveButton", {ns: "common"})}
            cancelText={t("modal.cancelButton2", {ns: "common"})}
        >
            <Form labelCol={{span: 24}} wrapperCol={{span: 24}}>
                <Row>
                    <Col span={24}>
                        <Form.Item
                            validateStatus={
                                errors.creditAmount && touched.creditAmount ? "error" : "success"
                            }
                            help={
                                errors.creditAmount && touched.creditAmount
                                    ? errors.creditAmount
                                    : null
                            }
                        >
                            <div style={{marginBottom: "20px"}}>
                                {values?.transactionPhoto ? (
                                    <div
                                        style={{display: "flex", gap: "15px", alignItems: "center"}}
                                    >
                                        <span>{values?.transactionPhoto?.name}</span>
                                        <DeleteOutlined
                                            onClick={() => setFieldValue("transactionPhoto", null)}
                                        />
                                    </div>
                                ) : (
                                    <Button
                                        type="primary"
                                        icon={<UploadOutlined />}
                                        onClick={fileUpload}
                                    >
                                        Fatura Yükle
                                    </Button>
                                )}
                            </div>

                            <h4>
                                {t("subcompanyDetail.credit.modals.creditAmount", {
                                    ns: "subcompanyDetail"
                                })}
                            </h4>
                            <Input
                                type="number"
                                name="creditAmount"
                                onChange={handleChange}
                                value={values.creditAmount}
                                data-testid="creditAmountInput"
                            />
                        </Form.Item>
                        <Form.Item
                            validateStatus={
                                errors.description && touched.description ? "error" : "success"
                            }
                            help={
                                errors.description && touched.description
                                    ? errors.description
                                    : null
                            }
                        >
                            <h4>Açıklama</h4>
                            <Input
                                type="text"
                                name="description"
                                onChange={handleChange}
                                value={values.description}
                                data-testid="descriptionInput"
                            />
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
            <UploadInvoiceModal
                visibility={updateFileUpdateVisibilty}
                onCancel={closeModals}
                setFieldValue={setFieldValue}
            />
        </Modal>
    );
};
